import { useCallback, useEffect, useRef } from "react";
import { clarity } from "react-microsoft-clarity";

import { useCookieConsent } from "./useCookieConsent";
import { useVenueGroupConfig } from "../hooks/useOrganisationData";
import { useAppSelector } from "../store/hooks";
import { selectCollinsUser } from "../store/slices/sessionSlice";
import {
  initialiseTelemetry,
  reportThirdPartyInitError,
  setTelemetryLabels,
  setUserContext
} from "../telemetry/telemetry";
import { dmnIdentity } from "../utils/dmnIdentity";
import { getEnvironmentProps } from "../utils/environment";
import { getBuildNumber } from "../utils/nativeUtils/customActions";

import { useVenueConfig } from "./useVenueData";
const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;
const RESIZE_DEBOUNCE_MS = 500;

/**
 * Sets Coralogix RUM user context and labels so that every subsequent
 * event can be correlated with the active user, venue, and application
 * metadata.
 *
 * User identity is registered via `setUserContext` which maps to
 * `CoralogixRum.setUserContext()`. All other metadata (venue, app
 * version, viewport) is registered via `setTelemetryLabels` which
 * maps to `CoralogixRum.setLabels()`.
 *
 * Also initialises Microsoft Clarity (session-replay) — this is
 * unrelated to the Coralogix migration and is preserved as-is.
 */
export const useTelemetry = () => {
  const user = useAppSelector(selectCollinsUser);
  const venueGroupConfig = useVenueGroupConfig();
  const venueConfig = useVenueConfig();
  const { consent } = useCookieConsent();

  const previousUserId = useRef<string | undefined>(undefined);
  const previousVenueGroupId = useRef<string | undefined>(undefined);
  const previousVenueId = useRef<string | undefined>(undefined);
  const clarityInitialised = useRef<boolean>(false);
  const telemetryInitialised = useRef<boolean>(false);
  const viewportAttributesAdded = useRef<boolean>(false);

  useEffect(() => {
    if (
      !consent.isResolved ||
      !consent.analytics ||
      telemetryInitialised.current
    )
      return;

    telemetryInitialised.current = true;
    initialiseTelemetry().catch((error) => {
      console.error("Failed to initialise telemetry:", error);
    });
  }, [consent.isResolved, consent.analytics]);

  useEffect(() => {
    if (!consent.analytics || !user?._id) return;
    if (previousUserId.current === user._id) return;

    previousUserId.current = user._id;

    try {
      const userName = [user.first_name, user.last_name]
        .filter(Boolean)
        .join(" ");

      setUserContext(user._id, userName || "Unknown", user.email);
    } catch (error) {
      console.error("Failed to set user telemetry context:", error);
    }
  }, [user, consent.analytics]);

  useEffect(() => {
    if (!consent.analytics || !venueGroupConfig?._id) return;
    if (previousVenueGroupId.current === venueGroupConfig._id) return;

    previousVenueGroupId.current = venueGroupConfig._id;

    try {
      const labels: Record<string, string> = {
        "Venue-Group-ID": venueGroupConfig._id
      };

      if (venueGroupConfig.title) {
        labels["Venue-Group-Name"] = venueGroupConfig.title;
      }

      setTelemetryLabels(labels);
    } catch (error) {
      console.error("Failed to set venue group telemetry labels:", error);
    }
  }, [venueGroupConfig, consent.analytics]);

  useEffect(() => {
    if (!consent.analytics || !venueConfig?._id) return;
    if (previousVenueId.current === venueConfig._id) return;

    previousVenueId.current = venueConfig._id;

    try {
      const labels: Record<string, string> = {
        "Venue-ID": venueConfig._id
      };

      if (venueConfig.title) {
        labels["Venue-Name"] = venueConfig.title;
      }

      setTelemetryLabels(labels);
    } catch (error) {
      console.error("Failed to set venue telemetry labels:", error);
    }
  }, [venueConfig, consent.analytics]);

  const updateViewportLabels = useCallback(() => {
    try {
      const width = window.innerWidth;
      const height = window.innerHeight;

      let viewportCategory = "desktop";
      if (width < MOBILE_BREAKPOINT) {
        viewportCategory = "mobile";
      } else if (width < TABLET_BREAKPOINT) {
        viewportCategory = "tablet";
      }

      setTelemetryLabels({
        "Viewport-Width": String(width),
        "Viewport-Height": String(height),
        "Viewport-Size": `${width}x${height}`,
        "Viewport-Category": viewportCategory
      });

      viewportAttributesAdded.current = true;
    } catch (error) {
      console.error("Failed to set viewport telemetry labels:", error);
    }
  }, []);

  useEffect(() => {
    if (!viewportAttributesAdded.current) {
      updateViewportLabels();
    }

    let debounceTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        updateViewportLabels();
      }, RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener("resize", handleResize);
    };
  }, [updateViewportLabels]);

  useEffect(() => {
    getBuildNumber()
      .then((version) => {
        setTelemetryLabels({ "App-Version": version });
      })
      .catch((error: unknown) => {
        console.warn("Failed to read build number", error);
      });
  }, []);

  useEffect(() => {
    if (
      !consent.analytics ||
      !user?._id ||
      clarity.hasStarted() ||
      clarityInitialised.current
    )
      return;

    clarityInitialised.current = true;

    const initialiseClaritySession = async () => {
      const token = await dmnIdentity.getAccessToken();
      const env = getEnvironmentProps(token);

      if (!env?.REACT_APP_CLARITY_TOKEN) {
        console.error("Clarity token is missing");
        return;
      }

      clarity.init(env.REACT_APP_CLARITY_TOKEN);
      clarity.identify(String(user._id), {});
      clarity.consent();
    };

    initialiseClaritySession().catch((error: unknown) => {
      console.error("Failed to initialise Clarity:", error);
      reportThirdPartyInitError("Clarity initialisation failed", error);
    });
  }, [user, consent.analytics]);
};
